<template>
  <div class="result-display">
    <div v-if="loading" class="loading-state">
      <div class="loading-animation">
        <div class="loading"></div>
      </div>
      <h3>🎨 AI正在处理您的图片...</h3>
      <p>请稍等片刻，这可能需要几十秒的时间</p>
      <div class="loading-tips">
        <p class="tip">💡 提示：如果处理失败，系统会自动重试最多8次</p>
        <p class="tip">🔄 每次重试会有延迟以保证成功率</p>
      </div>
    </div>

    <div v-else-if="error" class="error-state">
      <div class="error-icon">❌</div>
      <h3>处理失败</h3>
      <p class="error-message">{{ error }}</p>
      <button class="btn" @click="$emit('retry')">重试</button>
    </div>

    <div v-else-if="images && images.length > 0" class="success-state">
      <h3>✨ 处理完成！</h3>
      <div class="result-grid">
        <div v-for="(image, index) in images" :key="index" class="result-item">
          <div class="image-container">
            <img
              :src="image"
              :alt="`处理结果 ${index + 1}`"
              @load="onImageLoad"
              @error="onImageError"
            />
            <div class="image-overlay">
              <button class="overlay-btn" @click="downloadImage(image, index)">
                <span>💾</span>
                下载
              </button>
              <button class="overlay-btn" @click="viewFullscreen(image)">
                <span>🔍</span>
                查看
              </button>
            </div>
          </div>
          <p class="image-caption">结果图片 {{ index + 1 }}</p>
        </div>
      </div>

      <div class="result-actions">
        <button class="btn btn-secondary" @click="$emit('new-process')">处理新图片</button>
        <button class="btn" @click="downloadAll">下载全部</button>
      </div>
    </div>

    <!-- 全屏查看模态框 -->
    <div v-if="fullscreenImage" class="fullscreen-modal" @click="closeFullscreen">
      <div class="fullscreen-content" @click.stop>
        <img :src="fullscreenImage" alt="全屏查看" />
        <button class="close-btn" @click="closeFullscreen">×</button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResultDisplay',
  props: {
    loading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    images: {
      type: Array,
      default: () => []
    }
  },
  emits: ['retry', 'new-process'],
  data () {
    return {
      fullscreenImage: null,
      loadedImages: 0
    }
  },
  methods: {
    onImageLoad () {
      this.loadedImages++
    },
    onImageError (event) {
      console.error('图片加载失败:', event.target.src)
      event.target.alt = '图片加载失败'
      event.target.style.background = '#f8f9fa'
    },
    async downloadImage (imageUrl, index) {
      try {
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)

        const link = document.createElement('a')
        link.href = url
        link.download = `processed-image-${index + 1}.jpg`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        window.URL.revokeObjectURL(url)
      } catch (error) {
        console.error('下载图片失败:', error)
        alert('下载失败，请尝试右键保存图片')
      }
    },
    async downloadAll () {
      for (let i = 0; i < this.images.length; i++) {
        await this.downloadImage(this.images[i], i)
        // 添加小延迟避免浏览器阻止多个下载
        if (i < this.images.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    },
    viewFullscreen (imageUrl) {
      this.fullscreenImage = imageUrl
    },
    closeFullscreen () {
      this.fullscreenImage = null
    }
  },
  watch: {
    images () {
      this.loadedImages = 0
    }
  }
}
</script>

<style scoped>
.result-display {
  width: 100%;
  text-align: center;
}

.loading-state,
.error-state,
.success-state {
  padding: 40px 20px;
}

.loading-animation {
  margin-bottom: 24px;
}

.loading-state h3 {
  color: #74b9ff;
  margin-bottom: 12px;
}

.loading-state p {
  color: #6c757d;
}

.loading-tips {
  margin-top: 24px;
  padding: 16px;
  background: rgba(116, 185, 255, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(116, 185, 255, 0.2);
}

.loading-tips .tip {
  margin: 8px 0;
  font-size: 14px;
  color: #495057;
}

.error-state {
  color: #dc3545;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
}

.error-message {
  color: #6c757d;
  margin-bottom: 24px;
  line-height: 1.6;
}

.success-state h3 {
  color: #28a745;
  margin-bottom: 32px;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
}

.result-item {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.result-item:hover {
  transform: translateY(-4px);
}

.image-container {
  position: relative;
  overflow: hidden;
}

.image-container img {
  width: 100%;
  max-width: 100%;
  height: auto;
  display: block;
  transition: transform 0.3s ease;
}

.image-container:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-container:hover .image-overlay {
  opacity: 1;
}

.overlay-btn {
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 8px;
  padding: 12px 16px;
  cursor: pointer;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.overlay-btn:hover {
  background: white;
  transform: scale(1.05);
}

.image-caption {
  padding: 16px;
  margin: 0;
  color: #495057;
  font-weight: 600;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.fullscreen-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.fullscreen-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.fullscreen-content img {
  max-width: 100%;
  max-height: 100%;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
}

.close-btn {
  position: absolute;
  top: -40px;
  right: 0;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 768px) {
  .result-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .result-actions {
    flex-direction: column;
    align-items: center;
  }

  .result-actions .btn {
    width: 100%;
    max-width: 200px;
  }

  .fullscreen-modal {
    padding: 10px;
  }

  .close-btn {
    top: -30px;
    width: 28px;
    height: 28px;
    font-size: 16px;
  }
}
</style>
